"use client";

import { useMemo, useState } from "react";
import type { Session } from "next-auth";
import { useSession } from "next-auth/react";

import type { BlogArticle } from "@/types";
import {
	useGetSavedBlogsQuery,
	useSetSavedBlogsMutation,
} from "@/features/saved/savedApiInjections";

import { useToast } from "@/components/ui/use-toast";
import MediaCarousel from "@/components/carousels/MediaCarousel";
import SignupPromptModal from "@/components/auth/SignupPromptModal";
import { errorIntoString } from "@/lib/utils";
import { ga4SaveItem } from "@/lib/analytics/ga4-ecommerce";

type Props = { session?: Session | null; blog: BlogArticle };

export default function BlogMediaCarousel({ session: sessionProp, blog }: Props) {
	const { data: clientSession } = useSession();
	const session = sessionProp !== undefined ? sessionProp : (clientSession ?? null);
	const { toast } = useToast();
	const [showSignupModal, setShowSignupModal] = useState(false);

	// CRITICAL: Memoize the user ID; empty string must not run the query (skip + RTK arg).
	const userId = useMemo(() => {
		const raw = session?.user?.id;
		if (raw == null) return null;
		const s = String(raw).trim();
		return s.length > 0 ? s : null;
	}, [session?.user?.id]);
	
	// Only fetch if user is signed in and has a valid session token
	const hasValidSession = session?.idToken && session.idToken.trim().length > 0;
	
	const { data: saved, isLoading: isFavoriteLoading } = useGetSavedBlogsQuery(
		userId ?? "",
		{ 
			skip: userId == null || !hasValidSession,
			refetchOnMountOrArgChange: false,
			refetchOnFocus: false,
			refetchOnReconnect: false,
		},
	);
	const [setFav, { isLoading: isFavoriteSetting }] = useSetSavedBlogsMutation(undefined);
	const blogSharePath = `/blogs/${blog.slug || blog._id}`;
	const blogReturnUrl = process.env.NEXT_PUBLIC_FRONTEND_URI
		? `${process.env.NEXT_PUBLIC_FRONTEND_URI}${blogSharePath}`
		: blogSharePath;

	const isFavorite = useMemo(
		() =>
			saved === undefined ? false : saved.blogIds.findIndex((x) => x._id === blog._id) !== -1,
		[saved, blog._id],
	);

	const handleFavoriteClick = async () => {
		if (!session || !hasValidSession) {
			setShowSignupModal(true);
			return;
		}

		if (saved === undefined) return;

		try {
			if (isFavorite) {
				await setFav({
					userId: session.user.id,
					blogIds: [...saved?.blogIds.map((id) => id._id).filter((id) => id !== blog.id)],
				});

				ga4SaveItem({
					content_type: "blog",
					item_id: blog._id,
					item_name: blog.title,
					saved: false,
				});
				return toast({ title: `${blog.title} removed from saved` });
			}

			await setFav({
				userId: session.user.id,
				blogIds: [...saved?.blogIds.map((id) => id._id), blog._id],
			});

			ga4SaveItem({
				content_type: "blog",
				item_id: blog._id,
				item_name: blog.title,
				saved: true,
			});
			toast({ title: `${blog.title} saved` });
		} catch (error) {
			const message = errorIntoString(error);

			toast({
				variant: "destructive",
				title: "Failed to save this blog",
				description: message,
			});
		}
	};

	return (
		<>
			<MediaCarousel
				media={blog.media}
				isFavorite={isFavorite}
				isFavoriteLoading={isFavoriteLoading || isFavoriteSetting}
				onFavoriteClick={handleFavoriteClick}
				shareLink={blogReturnUrl}
				priorityFirstImage={true}
				revealCarouselChromeOnHoverTouch
			/>
			<SignupPromptModal
				open={showSignupModal}
				onOpenChange={setShowSignupModal}
				returnUrl={blogReturnUrl}
				title="Sign up to save this blog"
				description="Create a Techxeed account before saving this blog."
			/>
		</>
	);
}
